// Org-wide display preferences (Organization Settings → General): date format
// and week start, applied app-wide. The org admin picks them once; every
// member's browser fetches them on load (cached in localStorage for instant
// startup) and the shared helpers below render accordingly.
//
// formatDate() replaces bare `.toLocaleDateString()` callsites — rich formats
// (e.g. "Mon, Sep 7") deliberately keep their own toLocaleDateString(options).

export interface OrgFormatPrefs {
  dateFormat: string; // 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD'
  weekStart: 'monday' | 'sunday';
}

const STORE_KEY = 'kazi-org-format';
const DEFAULTS: OrgFormatPrefs = { dateFormat: 'DD/MM/YYYY', weekStart: 'monday' };

let prefs: OrgFormatPrefs = { ...DEFAULTS };
try {
  const raw = localStorage.getItem(STORE_KEY);
  if (raw) prefs = { ...DEFAULTS, ...JSON.parse(raw) };
} catch {
  /* defaults until the fetch lands */
}

// Reactivity: the boot fetch (and the admin Settings save) can update prefs AFTER
// components have mounted. prefs is a module singleton, not React state, so we
// notify subscribers on change; useOrgFormat() lets a surface re-render then —
// otherwise the async org-prefs fetch would land but the bell/notifications/
// dashboard would keep rendering the stale default until the next navigation.
let version = 0;
const listeners = new Set<() => void>();
export function subscribeOrgFormat(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}
export function orgFormatVersion(): number {
  return version;
}

export function setOrgFormatPrefs(p: Partial<OrgFormatPrefs> | null): void {
  const next = { ...DEFAULTS, ...(p || {}) };
  const changed = next.dateFormat !== prefs.dateFormat || next.weekStart !== prefs.weekStart;
  prefs = next;
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(prefs));
  } catch {
    /* in-memory copy still applies this session */
  }
  if (changed) {
    version += 1;
    listeners.forEach((l) => l());
  }
}

export function getOrgFormatPrefs(): OrgFormatPrefs {
  return prefs;
}

/** A bare civil date with no time/zone, e.g. "2026-09-09". */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Parse a display value to a Date, anchoring a CIVIL date (date-only string) to
 *  LOCAL midnight. `new Date("2026-09-09")` parses as UTC midnight, which renders
 *  as the previous day for any UTC-negative viewer — shifting hire dates, DOBs
 *  and leave dates. A date-only value is not an instant; anchor it locally so it
 *  reads the same for every viewer. Real instants (full ISO datetimes, epochs,
 *  Date objects) are left untouched and still render in the viewer's zone. */
export function toDisplayDate(d: Date | string | number): Date {
  if (typeof d === 'string' && DATE_ONLY_RE.test(d)) return new Date(`${d}T00:00:00`);
  return d instanceof Date ? d : new Date(d);
}

/** Render a date in the org's chosen numeric format. Accepts Date | ISO string |
 *  epoch; returns '' for empty and falls back to the browser locale on parse
 *  failure (never throws — these are display helpers). Civil dates (date-only
 *  strings) are anchored to local midnight so they never shift a day by zone. */
export function formatDate(d: Date | string | number | null | undefined): string {
  if (d === null || d === undefined || d === '') return '';
  // US-19: a legacy all-zeros civil date ("0000-00-00", from old imports) is not a
  // real date — render empty rather than a meaningless "0/0/0".
  if (typeof d === 'string' && /^0000-00-00/.test(d.trim())) return '';
  const date = toDisplayDate(d);
  if (Number.isNaN(date.getTime())) return String(d);
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = String(date.getFullYear());
  switch (prefs.dateFormat) {
    case 'MM/DD/YYYY':
      return `${mm}/${dd}/${yyyy}`;
    case 'YYYY-MM-DD':
      return `${yyyy}-${mm}-${dd}`;
    case 'DD/MM/YYYY':
      return `${dd}/${mm}/${yyyy}`;
    default:
      return date.toLocaleDateString();
  }
}

/** Org date + 24h time (e.g. "07/09/2026 14:05"). */
export function formatDateTime(d: Date | string | number | null | undefined): string {
  if (d === null || d === undefined || d === '') return '';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return String(d);
  const hh = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${formatDate(date)} ${hh}:${mi}`;
}

/** JS getDay() value the org week starts on: 1 (Monday, default) or 0 (Sunday). */
export function weekStartsOn(): 0 | 1 {
  return prefs.weekStart === 'sunday' ? 0 : 1;
}

/** Weekday header labels rotated to the org week start. */
export function weekdayLabels(): string[] {
  const sun = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return weekStartsOn() === 1 ? [...sun.slice(1), sun[0]] : sun;
}

/** Column offset (0-6) of a JS getDay() value inside the org-ordered week —
 *  use for the leading blanks in month grids. */
export function weekdayOffset(jsGetDay: number): number {
  return (jsGetDay - weekStartsOn() + 7) % 7;
}
